import { apiClient } from './client';

/**
 * Enterprise SSO (ENTERPRISE-SSO).
 *
 * The login form probes this before showing the password step: given an
 * email, is the address's domain wired to a tenant's IdP? If so the server
 * returns the broker authorize URL and the SPA does a *full-page* redirect
 * to it (SSO can't ride the in-page axios flow — it's a browser navigation
 * out to the IdP and back to the server callback, which then 302s us to
 * /dashboard with a fresh refresh cookie).
 */

export interface SsoAuthorizeResponse {
  /** True when the email's domain resolves to an active SSO connection. */
  sso: boolean;
  /** The broker authorize URL to redirect to; present only when sso=true. */
  authorize_url?: string | null;
  /** When true the tenant mandates SSO — hide the password fallback. */
  enforce: boolean;
}

/**
 * Probe whether `email`'s domain is SSO-enabled. Never throws for the
 * "not SSO" case — the server answers `{ sso: false }`; a network error
 * bubbles up and the caller falls back to password login.
 */
export async function ssoAuthorize(
  email: string,
): Promise<SsoAuthorizeResponse> {
  const { data } = await apiClient.get<SsoAuthorizeResponse>(
    '/api/v1/auth/sso/authorize',
    { params: { email } },
  );
  return data;
}

// ---------------------------------------------------------------------
// Admin config (ENTERPRISE-SSO Phase 2) — superadmin-owned CRUD of a
// tenant's SSO connection. Surfaced on Configuration → Enterprise SSO
// (tenant picker) and on the superadmin Tenant Detail page.
// ---------------------------------------------------------------------

export interface SsoBrokerStatus {
  /** True when the API carries WORKOS_API_KEY + client id + redirect URI. */
  configured: boolean;
  /** The callback URL registered with the broker; null when unset. */
  redirect_uri: string | null;
}

/**
 * Is the SSO broker wired up on the API at all (superadmin only)? A tenant
 * connection does nothing until it is, and the credentials live in the
 * environment / SSM rather than in the portal — so the config UI reads this
 * to explain a dark feature instead of leaving an admin guessing.
 */
export async function getSsoBrokerStatus(): Promise<SsoBrokerStatus> {
  const { data } = await apiClient.get<SsoBrokerStatus>(
    '/api/v1/auth/sso/broker-status',
  );
  return data;
}

/** A role a JIT-provisioned SSO user may be granted — never superadmin. */
export type AssignableRole = 'tenant_admin' | 'coordinator' | 'participant';

export interface SsoConnection {
  id: string;
  tenant_id: string;
  /** The broker's (WorkOS) connection identifier for this tenant's IdP. */
  broker_connection_id: string;
  /** Email domains that route to this connection at login (lowercased). */
  allowed_domains: string[];
  /** Role granted when no IdP group matches. */
  default_role: AssignableRole;
  /** IdP-group → role overrides, most-privileged wins. */
  group_role_map: Record<string, AssignableRole>;
  /** When true, users on these domains cannot use password login. */
  enforce_sso: boolean;
  /** When true, SSO counts as MFA (no separate TOTP challenge). */
  sso_satisfies_mfa: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface SsoConnectionUpsert {
  broker_connection_id: string;
  allowed_domains: string[];
  default_role: AssignableRole;
  group_role_map: Record<string, AssignableRole>;
  enforce_sso: boolean;
  sso_satisfies_mfa: boolean;
  is_active: boolean;
}

/**
 * Fetch a tenant's SSO connection. Rejects with a 404 when none is
 * configured — callers treat that as "not set up yet", not an error.
 */
export async function getSsoConnection(
  tenantId: string,
): Promise<SsoConnection> {
  const { data } = await apiClient.get<SsoConnection>(
    `/api/v1/tenants/${tenantId}/sso-connection`,
  );
  return data;
}

/** Create or replace a tenant's SSO connection (superadmin only). */
export async function putSsoConnection(
  tenantId: string,
  body: SsoConnectionUpsert,
): Promise<SsoConnection> {
  const { data } = await apiClient.put<SsoConnection>(
    `/api/v1/tenants/${tenantId}/sso-connection`,
    body,
  );
  return data;
}

/** Remove a tenant's SSO connection (superadmin only). */
export async function deleteSsoConnection(tenantId: string): Promise<void> {
  await apiClient.delete(`/api/v1/tenants/${tenantId}/sso-connection`);
}
